import { apiClient } from '@/shared/api';
import type {
  DownloadTemplateEntityType,
  DownloadTemplateItem,
  DownloadTemplateListResponse,
  DownloadTemplateUploadRequest,
  DownloadTemplateVariablesResponse,
} from './types';

/**
 * Перечень шаблонов сущности (GET /download-template).
 * Доступен любому аутентифицированному: перечень нужен и разделу «Шаблоны», и выбору шаблона
 * при скачивании.
 *
 * `preserveParamsCase` — контракт эндпоинта задаёт условие как `entityType`, а не kebab-case.
 */
export const fetchDownloadTemplates = async (
  entityType: DownloadTemplateEntityType,
): Promise<DownloadTemplateListResponse> => {
  const { data } = await apiClient.get<DownloadTemplateListResponse>('/download-template', {
    params: { entityType },
    preserveParamsCase: true,
  });
  return data;
};

/**
 * Переменные, которые система подставляет в шаблон сущности, и примеры их записи
 * (GET /download-template/variables).
 */
export const fetchDownloadTemplateVariables = async (
  entityType: DownloadTemplateEntityType,
): Promise<DownloadTemplateVariablesResponse> => {
  const { data } = await apiClient.get<DownloadTemplateVariablesResponse>('/download-template/variables', {
    params: { entityType },
    preserveParamsCase: true,
  });
  return data;
};

/** Загрузка файла шаблона с его наименованием (POST /download-template) */
export const uploadDownloadTemplate = async ({
  entityType,
  name,
  file,
}: DownloadTemplateUploadRequest): Promise<DownloadTemplateItem> => {
  const formData = new FormData();
  formData.append('entityType', entityType);
  formData.append('name', name);
  formData.append('file', file);

  const { data } = await apiClient.post<DownloadTemplateItem>('/download-template', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

/**
 * Файл шаблона как он был загружен (GET /download-template/{id}/file) — образец, по которому
 * заводят свой шаблон.
 */
export const downloadTemplateFile = async (id: number): Promise<Blob> => {
  const { data } = await apiClient.get<Blob>(`/download-template/${id}/file`, { responseType: 'blob' });
  return data;
};

/** Удаление шаблона вместе с его файлом (DELETE /download-template/{id}) */
export const deleteDownloadTemplate = async (id: number): Promise<void> => {
  await apiClient.delete(`/download-template/${id}`);
};
